// v2/mentions.tsx — Sistema de @menções (FP4). Núcleo compartilhado: formato do
// token, parser texto→segmentos, chip clicável e navegação por contexto (sem
// prop-drilling). Render aqui (compose/autocomplete vive em mention-input.tsx).
import React, { createContext, useContext } from 'react';

export type MentionType = 'user' | 'book' | 'club';

// Token canônico embutido no texto: @[Label](tipo:uuid). Usado p/ LIVRO e CLUBE
// (sem handle legível) + menções de usuário LEGADAS. Carrega id + label.
export const MENTION_RE = /@\[([^\]]+)\]\((user|book|club):([a-f0-9-]{36})\)/g;
// Menção a USUÁRIO no formato limpo @username (estilo Twitter) — o que a composição
// mostra. Resolve por handle em runtime (sem id no texto). Fronteira evita e-mail
// (a@b) e o lookahead evita colidir com o token canônico (@[).
export const USER_HANDLE_RE = /(^|[^\p{L}\p{N}_@])@([\p{L}\p{N}_][\p{L}\p{N}_.]*[\p{L}\p{N}_]|[\p{L}\p{N}_])(?![\p{L}\p{N}_[])/gu;

export interface MentionTok { kind: 'mention'; type: MentionType; id?: string; handle?: string; label: string }
export interface TextSeg { kind: 'text'; value: string }

// Quebra o texto em segmentos de texto puro / menção. Dois passes: 1) tokens
// canônicos (livro/clube/legado), 2) @username plano sobre os trechos de texto.
// RegExp fresca por chamada (evita bug de lastIndex no /g compartilhado).
export function splitMentions(text: string): (TextSeg | MentionTok)[] {
  const s = text || '';
  // Passe 1 — token canônico
  const canon: (TextSeg | MentionTok)[] = [];
  const re = new RegExp(MENTION_RE.source, 'g');
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(s))) {
    if (m.index > last) canon.push({ kind: 'text', value: s.slice(last, m.index) });
    canon.push({ kind: 'mention', label: m[1], type: m[2] as MentionType, id: m[3] });
    last = m.index + m[0].length;
  }
  if (last < s.length) canon.push({ kind: 'text', value: s.slice(last) });
  // Passe 2 — @username plano dentro de cada trecho de TEXTO
  const out: (TextSeg | MentionTok)[] = [];
  canon.forEach((seg) => {
    if (seg.kind !== 'text') { out.push(seg); return; }
    const t = seg.value;
    const reH = new RegExp(USER_HANDLE_RE.source, 'gu');
    let l2 = 0;
    let h: RegExpExecArray | null;
    while ((h = reH.exec(t))) {
      const boundary = h[1] || '';
      const handle = h[2];
      const at = h.index + boundary.length; // posição do '@'
      if (at > l2) out.push({ kind: 'text', value: t.slice(l2, at) });
      out.push({ kind: 'mention', type: 'user', handle, label: handle });
      l2 = at + 1 + handle.length; // '@' + handle
    }
    if (l2 < t.length) out.push({ kind: 'text', value: t.slice(l2) });
  });
  return out;
}

// Extrai só as menções (pra o backend notificar / persistir).
export function extractMentions(text: string): MentionTok[] {
  return splitMentions(text).filter((x): x is MentionTok => x.kind === 'mention');
}

// Trunca por COMPRIMENTO EXIBIDO (texto puro + "@label"), nunca cortando um token
// de menção no meio. Usado em previews que faziam slice() cru sobre o texto com
// tokens (ex.: inner.tsx). `clamped` = true se sobrou conteúdo escondido.
export function splitMentionsClamped(text: string, max: number): { segs: (TextSeg | MentionTok)[]; clamped: boolean } {
  const all = splitMentions(text || '');
  const out: (TextSeg | MentionTok)[] = [];
  let used = 0;
  let clamped = false;
  for (const seg of all) {
    if (seg.kind === 'mention') {
      const len = (seg.label ? seg.label.length : 0) + 1; // "@label"
      if (used + len > max) { clamped = true; break; }
      out.push(seg); used += len;
    } else {
      const room = max - used;
      if (seg.value.length > room) { out.push({ kind: 'text', value: seg.value.slice(0, Math.max(0, room)) }); clamped = true; break; }
      out.push(seg); used += seg.value.length;
    }
  }
  return { segs: out, clamped };
}

// Monta o token (sanitiza o label de caracteres que quebram a sintaxe).
export function mentionToken(type: MentionType, id: string, label: string): string {
  const safe = String(label || '').replace(/[[\]()]/g, '').replace(/\s+/g, ' ').trim().slice(0, 60) || '?';
  return `@[${safe}](${type}:${id})`;
}

// ───────── Navegação por contexto (App injeta uma vez na raiz) ─────────
interface MentionNav { openProfile?: (id: string) => void; openProfileByHandle?: (handle: string) => void; openBook?: (id: string) => void; openClub?: (id: string) => void; }
const MentionNavContext = createContext<MentionNav>({});
export const MentionNavProvider: React.FC<{ value: MentionNav; children: React.ReactNode }> = ({ value, children }) => (
  <MentionNavContext.Provider value={value}>{children}</MentionNavContext.Provider>
);
export const useMentionNav = () => useContext(MentionNavContext);

// ───────── Dados de autocomplete por contexto (App injeta uma vez) ─────────
// Evita passar people/books/clubs por prop em cada superfície. MentionInput lê daqui.
interface MentionData { people?: any[]; books?: any[]; clubs?: any[] }
const MentionDataContext = createContext<MentionData>({});
export const MentionDataProvider: React.FC<{ value: MentionData; children: React.ReactNode }> = ({ value, children }) => (
  <MentionDataContext.Provider value={value}>{children}</MentionDataContext.Provider>
);
export const useMentionData = () => useContext(MentionDataContext);

// ───────── Chip clicável ─────────
// clickable=false (privacidade: bloqueado / clube fechado a não-membro) → vira
// só texto colorido, sem navegação.
export const MentionChip: React.FC<{ type: MentionType; id?: string; handle?: string; label: string; clickable?: boolean }> = ({ type, id, handle, label, clickable = true }) => {
  const nav = useMentionNav();
  const byHandle = type === 'user' && !!handle && !!nav.openProfileByHandle;
  const handler = type === 'user' ? nav.openProfile : type === 'book' ? nav.openBook : nav.openClub;
  const canNav = clickable && (byHandle || (!!handler && !!id));
  const go = (e: React.MouseEvent) => {
    e.stopPropagation(); e.preventDefault();
    if (byHandle) { nav.openProfileByHandle!(handle!); return; }
    if (handler && id) handler(id);
  };
  return (
    <span
      onClick={canNav ? go : undefined}
      role={canNav ? 'button' : undefined}
      style={{ color: 'var(--ember-2, var(--amber, #e07a3b))', fontWeight: 600, cursor: canNav ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
    >@{label}</span>
  );
};

// ───────── Render de texto cru com menções (superfícies sem markdown) ─────────
// Drop-in pra trocar {texto} por <MentionText text={texto}/>. Preserva quebras
// conforme o CSS do container (mesmo comportamento de {texto}).
export const MentionText: React.FC<{ text?: string | null; style?: React.CSSProperties; className?: string }> = ({ text, style, className }) => {
  const segs = splitMentions(text || '');
  return (
    <span className={className} style={style}>
      {segs.map((seg, i) => seg.kind === 'mention'
        ? <MentionChip key={i} type={seg.type} id={seg.id} handle={seg.handle} label={seg.label} />
        : <React.Fragment key={i}>{seg.value}</React.Fragment>)}
    </span>
  );
};
